// src/pages/data/dataW/DataLG.jsx

import React, {useEffect, useState, useRef} from 'react';
import jwtAxios, {sseRequest} from '@crema/services/auth/jwt-auth';
import DataUI from './DataUI';
import AppInfoView from '@crema/core/AppInfoView';
import {useDispatch} from 'react-redux';
import {fetchError, showMessage} from 'redux/actions';
import {useAuthUser} from '@crema/utility/AuthHooks';
import {saveAs} from 'file-saver';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';

const DataLG = () => {
  const dispatch = useDispatch();

  // Pagination and Data State
  const [rowCount, setRowCount] = useState(50);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(0);
  const [dataToShow, setDataToShow] = useState([]);
  const [customerMapping, setCustomerMapping] = useState({});
  const [exportingModal, setExportingModal] = useState(false);

  // Filter State
  const [filters, setFilters] = useState({
    country: '',
    dataSource: '',
    domain: '',
    customer: '',
    search: '',
    dateRange: [null, null],
    sentDateRange: [null, null],
    integrationSource: '',
  });

  // Loading and Error States
  const [gettingData, setGettingData] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const [selected, setSelected] = useState([]);
  // Use ref to always have the latest selected values - preserve across data changes
  const selectedRef = useRef([]);
  // Track all selected IDs across all pages/filters (persistent selection)
  const allSelectedIdsRef = useRef([]);
  const [doneStatusCount, setDoneStatusCount] = useState(0);
  const [allStatusCount, setAllStatusCount] = useState(0);
  const [errorStatusCount, setErrorStatusCount] = useState(0);

  // Debug: Track selected state changes and update ref
  useEffect(() => {
    console.log('🟢 selected state changed to:', selected);
    selectedRef.current = selected;
  }, [selected]);

  // Fix 1: Reset selection whenever the data changes to prevent stale selection state
  useEffect(() => {
    setSelected([]);
    selectedRef.current = [];
    allSelectedIdsRef.current = [];
  }, [dataToShow]);

  // Modal States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [upLoadingData, setUpLoadingData] = useState(false);

  // Search and Selection States
  const [searchTerm, setSearchTerm] = useState('');
  const [customerId, setCustomerId] = useState(0);

  // Date formatting function
  const formatDate = (dateString) => {
    return formatDateToJerusalem(dateString);
  };
  const [selecteCustomer, setSelecteCustomer] = useState(false);
  const [affiliateId, setAffiliateId] = useState([]);
  const [affiliateIdWithoutNoApi, setAffiliateIdWithoutNoApi] = useState([]);

  // Error Handling
  const [errorData, setErrorData] = useState([]);

  // Domain States
  const [dominId, setDominId] = useState([]);
  const [domainSelected, setDomainSelected] = useState(false);
  const [domainBackendUrl, setDomainBackendUrl] = useState(null);

  // New Filter Options States
  const [scorcessOptions, setScorcessOptions] = useState([]);
  const [integrationSourceOptions, setIntegrationSourceOptions] = useState([]);

  // Patch Edited State
  const [patchEdited, setPatchEdited] = useState({});

  // Lead History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLeadId, setHistoryLeadId] = useState(null);
  const [historyIsDuplicate, setHistoryIsDuplicate] = useState(false);

  const user = useAuthUser();
  const [isFilterCardVisible, setIsFilterCardVisible] = useState(false); // default is visible

  // Whether to include re-sent (highlighted / duplicate-of-sent) rows — persisted in localStorage
  const [includeHighlighted, setIncludeHighlighted] = useState(true);

  // Restore the persisted choice on mount
  useEffect(() => {
    const saved = localStorage.getItem('dataAll.includeHighlighted');
    if (saved !== null) setIncludeHighlighted(saved === 'true');
  }, []);

  // Persist the choice and refetch from page 1 (the row set changes size)
  const handleIncludeHighlightedChange = (checked) => {
    setIncludeHighlighted(checked);
    localStorage.setItem('dataAll.includeHighlighted', String(checked));
    setPageNumber(0);
  };
  const [archiveData, setArchiveData] = useState({});
  const [uploadFinished, setUploadFinished] = useState(false); // New state to track upload completion
  const [domainIdSelected, setDomainIdSelected] = useState(null);
  // Preserve sorting between reloads
  const [sortModel, setSortModel] = useState([]);

  // New State for disableDataSource Flag
  const [disableDataSource, setDisableDataSource] = useState(false);

  // appliedFilters is what's actually fetched; filters is the input/display state
  const [appliedFilters, setAppliedFilters] = useState({
    country: '',
    dataSource: '',
    domain: '',
    customer: '',
    search: '',
    dateRange: [null, null],
    sentDateRange: [null, null],
    integrationSource: '',
  });

  // Request counter — only the latest request may update state
  const requestIdRef = useRef(0);
  // Always-current ref for filters — prevents stale-closure bugs in timers and event handlers
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  // Always-current ref for appliedFilters — used by direct getData() calls
  const appliedFiltersRef = useRef(appliedFilters);
  useEffect(() => { appliedFiltersRef.current = appliedFilters; }, [appliedFilters]);
  // Always-current ref for pageNumber — used by direct getData() calls
  const pageNumberRef = useRef(pageNumber);
  useEffect(() => { pageNumberRef.current = pageNumber; }, [pageNumber]);

  // Guard to skip debounce on initial mount
  const [isFilterMounted, setIsFilterMounted] = useState(false);
  useEffect(() => {
    setIsFilterMounted(true);
  }, []);

  // Fetch filter options + customers on mount (not tied to pagination)
  useEffect(() => {
    getFilterOptions();
    getCustomers(1000, 0, searchTerm);
  }, []);

  // THE ONLY effect that fetches data — fires when page, size, or applied filters change
  useEffect(() => {
    getData(pageNumber, pageSize, appliedFilters);
  }, [pageNumber, pageSize, appliedFilters, includeHighlighted]);

  // Filter debounce — batches page reset + appliedFilters update into ONE render
  useEffect(() => {
    if (!isFilterMounted) return;
    const timer = setTimeout(() => {
      const latestFilters = filtersRef.current; // always fresh, no stale closure
      setPageNumber(0);
      setAppliedFilters({...latestFilters});
    }, 500);
    return () => clearTimeout(timer);
  }, [
    filters.search,
    filters.country,
    filters.dataSource,
    filters.integrationSource,
    filters.dateRange,
    filters.domain,
    filters.customer,
    filters.sentDateRange,
  ]);

  useEffect(() => {
    getCustomersForSend(1000, 0, searchTerm);
  }, [showUploadModal]);

  // Fetch domains when the component mounts
  useEffect(() => {
    const getDataTimeout = setTimeout(() => {
      console.log('Start fetching domains');
      getDomains(10, 0, searchTerm);
    }, 1000);
    return () => clearTimeout(getDataTimeout);
  }, []);
  // Toggle the visibility of the filter card
  const toggleFilterCardVisibility = () => {
    setIsFilterCardVisible(!isFilterCardVisible);
  };

  // Fetch Data with Filters - Only show data sent to one customer
  const getData = (pageNumber, pageSize, filterParams = filters) => {
    console.log(filterParams);

    const thisRequestId = ++requestIdRef.current;
    setGettingData(true);
    setDisableDataSource(false);
    setDataToShow([]); // Fix 2: clear old rows immediately so stale data never shows
    // Construct query string based on filter parameters - only get data sent to one customer
    let query = `data/sent-to-single-customer?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    query += `&includeHighlighted=${includeHighlighted}`;
    if (filterParams.customer)
      query += `&customer=${encodeURIComponent(filterParams.customer)}`;
    if (filterParams.country)
      query += `&country=${encodeURIComponent(filterParams.country)}`;
    if (filterParams.domain)
      query += `&domainId=${encodeURIComponent(filterParams.domain)}`;
    if (filterParams.dataSource)
      query += `&dataSource=${encodeURIComponent(filterParams.dataSource)}`;
    if (filterParams.search)
      query += `&searchParamsData=${encodeURIComponent(filterParams.search)}`;
    if (filterParams.integrationSource)
      query += `&integrationSource=${encodeURIComponent(filterParams.integrationSource)}`;
    if (filterParams.dateRange) {
      const [startDate, endDate] = filterParams.dateRange;
      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          const dd = d instanceof Date ? new Date(d) : new Date(d);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };
      const from = normalizeToDayIso(startDate, false);
      const to = normalizeToDayIso(endDate, true);
      if (from && to) query += `&fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}`;
    }
    if (filterParams.sentDateRange) {
      const [startDate, endDate] = filterParams.sentDateRange;
      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          const dd = d instanceof Date ? new Date(d) : new Date(d);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };
      const from = normalizeToDayIso(startDate, false);
      const to = normalizeToDayIso(endDate, true);
      if (from && to) query += `&sentFromDate=${encodeURIComponent(from)}&sentToDate=${encodeURIComponent(to)}`;
    }
    jwtAxios
      .get(query)
      .then((result) => {
        if (thisRequestId !== requestIdRef.current) return; // stale — a newer request is in flight
        console.log(
          '🔍 Single customer sent data received from backend:',
          result.data,
        );
        console.log(
          '🔍 First single customer record:',
          result.data.data?.[0],
        );
        console.log(
          '🔍 Duplicate detection fields in first record:',
          result.data.data?.[0]
            ? {
                is_duplicate_of_sent:
                  result.data.data[0].is_duplicate_of_sent,
                duplicate_count: result.data.data[0].duplicate_count,
                duplicate_ids: result.data.data[0].duplicate_ids,
              }
            : 'No data',
        );
        setDataToShow(result.data.data.data || []);
        setRowCount(result.data.data.count || 0);
      })
      .catch((e) => {
        if (thisRequestId !== requestIdRef.current) return;
        dispatch(fetchError(e.message));
      })
      .finally(() => {
        if (thisRequestId === requestIdRef.current) setGettingData(false);
      });
  };
  // Fetch filter options from the backend
  // Fetch filter options from the backend
  const getFilterOptions = () => {
    jwtAxios
      .get('data/filterOptions') // Replace with your actual endpoint
      .then((response) => {
        console.log('🔍 Filter options response:', response.data);

        // Handle the correct data structure from backend
        const filterData = response.data.data;

        // Check if filterData is an array of objects
        if (Array.isArray(filterData)) {
          // Extract dataSources and countries from the array of objects
          const dataSources = filterData
            .map((item) => item.dataSource)
            .filter(Boolean);
          const countries = filterData
            .map((item) => item.country)
            .filter(Boolean);

          // Debug: Log the actual country data to see what we're getting
          console.log('🔍 Raw country data from backend:', countries);
          console.log('🔍 Sample country item:', countries[0]);
          console.log('🔍 Country item type:', typeof countries[0]);

          // Clean up response data for valid country options and data sources
          const scorcessSet = new Set(dataSources);

          // Process countries to handle both strings and objects
          const processedCountries = countries.map((country) => {
            if (typeof country === 'string') {
              return country;
            } else if (country && typeof country === 'object') {
              // Handle country objects - extract the label or name
              return (
                country.label ||
                country.name ||
                country.code ||
                JSON.stringify(country)
              );
            }
            return String(country);
          });

          const countriesSet = new Set(processedCountries);
          console.log('countriesSet');
          console.log(countriesSet);
          console.log('scorcessSet');
          console.log(scorcessSet);

          setScorcessOptions(
            [...scorcessSet].map((dataSource) => ({
              label: dataSource,
              key: dataSource,
            })),
          );
          
          // Extract integration sources
          const integrationSources = filterData
            .map((item) => item.integrationSource)
            .filter(Boolean);
          const integrationSourcesSet = new Set(integrationSources);
          setIntegrationSourceOptions(
            [...integrationSourcesSet].map((integrationSource) => ({
              label: integrationSource,
              key: integrationSource,
            })),
          );
        } else {
          // Fallback: try to access the data directly
          console.log('🔍 Filter data is not an array, trying direct access');
          const dataSources = filterData?.dataSources || [];

          setScorcessOptions(
            dataSources.map((dataSource) => ({
              label: dataSource,
              key: dataSource,
            })),
          );
          
          // Extract integration sources from fallback structure
          const integrationSources = filterData?.integrationSources || [];
          setIntegrationSourceOptions(
            integrationSources.map((integrationSource) => ({
              label: integrationSource,
              key: integrationSource,
            })),
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching filter options:', error);
        dispatch(fetchError(error.message));
      });
  };

  // useEffect to send mapped archiveData to the API
  useEffect(() => {
    if (uploadFinished) {
      console.log('Final archiveData:', archiveData); // Log final archiveData

      // Define the API endpoint using backticks for template literals
      const apiEndpoint = `/archive/add-api`;
      console.log('Sending archiveData to:', apiEndpoint);

      // Map archiveData to the required format with "id" and "status"
      const mappedArchiveData = Object.values(archiveData).map((entry) => ({
        id: entry.id,
        status: entry.status,
      }));

      // Prepare the data to send (ensure it matches the API's expected format)
      const dataToSend = {
        archive: mappedArchiveData, // Adjusted archive structure
        customer_id: customerId,
        domain_id: domainIdSelected,
      };
      console.log('Data to send:', dataToSend);

      // Make the POST request using jwtAxios
      jwtAxios
        .post(apiEndpoint, dataToSend)
        .then((response) => {
          console.log('API response:', response);

          // Handle successful response
          dispatch(showMessage('Archive data successfully sent.'));
          // Reset archiveData and uploadFinished
          setArchiveData({});
          setUploadFinished(false);
        })
        .catch((error) => {
          console.log('API error:', error);
          // Handle errors
          dispatch(fetchError('Failed to send archive data: ' + error.message));
        });
    }
  }, [uploadFinished, dispatch]);

  // Handle Applying Filters — reads from ref to avoid stale-closure bug
  const handleApplyFilters = () => {
    setPageNumber(0);
    setAppliedFilters({...filtersRef.current}); // always fresh, no stale closure
  };
  const exportingModalOpen = () => {
    setExportingModal(true);
  };

  const exportingModalClose = () => {
    setExportingModal(false);
  };
  // Remanage Archive Data
  const remanageDataArchive = (input) => {
    try {
      console.log('Incoming input:', input);

      // Ensure input is parsed
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;

      // Extract and parse inner `data`
      const rawData = parsedInput.data;
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

      // Safely extract ID and determine status
      const id = data?.id || 'Unknown ID';
      const status =
        parsedInput.error || (id === 'Unknown ID' ? 'Error' : 'Success');

      console.log('Parsed Archive Data:', {id, status});
      return {id, status};
    } catch (error) {
      console.error('Error parsing archive data:', error);
      return {id: 'Unknown ID', status: 'Error'};
    }
  };

  // Handle Customer Selection Change — use functional updater to avoid stale-closure bug
  const handleCustomerChange = (_, valueSelected) => {
    setFilters((prev) => {
      const newFilters = {...prev, customer: valueSelected?.key || ''};
      setAppliedFilters(newFilters);
      return newFilters;
    });
    setPageNumber(0);
  };

  // Handle Clearing Filters
  const handleClearFilters = () => {
    const empty = {
      country: '',
      dataSource: '',
      search: '',
      domain: '',
      customer: '',
      dateRange: [null, null],
      sentDateRange: [null, null],
      integrationSource: '',
    };
    setFilters(empty);
    setAppliedFilters(empty); // triggers main effect — no direct getData call
    setPageNumber(0);
  };

  // Handle Modal Close
  // Handle Modal Close
  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelecteCustomer(false);
    setDomainSelected(false);
    setDomainIdSelected(null);

    // Only clear archiveData if not uploading
    if (!upLoadingData) {
      setArchiveData({});
    }

    // Clear selection after successful send (optional - you may want to keep it)
    // Uncomment if you want to clear selection after sending:
    // setSelected([]);
    // selectedRef.current = [];
    // allSelectedIdsRef.current = [];

    // Refresh data after closing the sending modal
    getData(pageNumberRef.current, pageSize, appliedFiltersRef.current);
  };

  // Handle Domain Selection Change for FILTERS — use functional updater to avoid stale-closure bug
  const handleDomainChange = (_, valueSelected) => {
    setFilters((prev) => {
      const newFilters = {...prev, domain: String(valueSelected?.key || '')};
      setAppliedFilters(newFilters);
      return newFilters;
    });
    setPageNumber(0);
  };

  // Handle Domain Selection Change for SEND MODAL (separate from filters)
  const handleSendModalDomainChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setDomainSelected(true);
      const selectedDomain = dominId.find(
        (domain) => domain.key === valueSelected.key,
      );
      if (selectedDomain) {
        setDomainBackendUrl(selectedDomain.url); // Store the selected domain's backend URL
        console.log(selectedDomain.url); // Log the URL to silence the warning
        setDomainIdSelected(selectedDomain.key);
      }
      setCustomerId(null); // Clear customer ID
      getCustomers(1000, 0, searchTerm, valueSelected.key); // Fetch customers with the selected domain ID
    } else {
      setDomainSelected(false);
      setDomainIdSelected(null);
      setDomainBackendUrl(null); // Clear the backend URL
      setCustomerId(null); // Clear the customer ID
      setAffiliateId([]); // Clear customer list if no domain is selected
      onDropDownChange(_, valueSelected);
      setSelecteCustomer(false); // Hide AI dropdown
    }
  };

  // Handle other dropdowns (generic handler)
  const handleChange = (field) => (event, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value?.key || '',
    }));
  };

  // Helper function to format error messages into readable text
  const formatErrorMessage = (errorMessage) => {
    if (!errorMessage) return 'An error occurred';

    // Check for duplicate entry error with ID
    const duplicateIdMatch = errorMessage.match(/Duplicate lead ID:\s*(\d+)/);
    
    if (duplicateIdMatch) {
      const duplicateId = duplicateIdMatch[1];
      return `This data is duplicate of Lead ID: ${duplicateId}. Please check the existing lead before updating.`;
    }

    // Check for generic duplicate entry error
    if (errorMessage.includes('Duplicate entry') || errorMessage.includes('duplicate')) {
      return 'This data already exists in the system. Please check for duplicates.';
    }

    // Remove technical prefixes like "CANNOT_UPDATE_DATA:"
    let cleanMessage = errorMessage.replace(/^[A-Z_]+:\s*/g, '');
    
    // Simplify MySQL error messages
    cleanMessage = cleanMessage.replace(/for key ['"][\w.]+['"]/gi, '');
    cleanMessage = cleanMessage.replace(/Duplicate entry ['"][^'"]*['"]/gi, 'Duplicate data found');
    
    return cleanMessage;
  };

  // Handle Editing Data
  const onEditCommit = (rowId, editData = null) => {
    const flatingObject = {};
    let neededObject;

    // Use provided editData if available, otherwise use patchEdited state
    if (editData && editData[rowId]) {
      neededObject = editData[rowId];
    } else {
      neededObject = patchEdited[rowId];
    }

    // Check if neededObject exists and is not null/undefined
    if (!neededObject || typeof neededObject !== 'object') {
      console.error('No edit data found for row:', rowId);
      return;
    }

    Object.keys(neededObject).forEach((key) => {
      flatingObject[key] = neededObject[key].value;
    });

    flatingObject.id = rowId;

    jwtAxios
      .put(`data`, flatingObject)
      .then(() => {
        dispatch(
          showMessage(
            'Successfully updated data with ID = ' + flatingObject.id,
          ),
        );
        // Refresh the data table after successful update
        getData(pageNumberRef.current, pageSize, appliedFiltersRef.current);
      })
      .catch((e) => {
        // Extract the actual error message from the backend response
        const errorMessage = e.response?.data?.message || e.message || 'Failed to update data';
        // Format the error message to be user-friendly
        const readableMessage = formatErrorMessage(errorMessage);
        dispatch(fetchError(readableMessage));
      });
  };

  // Handle Row Selection
  const handleOnSelect = (values) => {
    console.log('🔵 handleOnSelect called with values:', values);
    console.log('🔵 Current selected state before update:', selected);
    console.log('🔵 Current allSelectedIdsRef:', allSelectedIdsRef.current);
    console.log('🔵 values type:', typeof values, 'isArray:', Array.isArray(values));
    
    // Get current visible row IDs to determine if this is a user action or data change
    const currentVisibleIds = dataToShow.map((row) => row.id);
    console.log('🔵 Current visible row IDs:', currentVisibleIds);
    
    // Handle different value types safely
    const modifiedValues = values
      .map((value) => {
        // If value is already a number, return it
        if (typeof value === 'number') {
          return value;
        }

        // If value is a string, try to split and extract number
        if (typeof value === 'string') {
          if (value.includes('-')) {
            return Number(value.split('-')[0].trim());
          } else {
            // If no '-', try to parse the string as a number
            return Number(value);
          }
        }

        // If value is an object with an id property, use that
        if (value && typeof value === 'object' && value.id) {
          return Number(value.id);
        }

        // Fallback: try to convert to number
        return Number(value);
      })
      .filter((value) => !isNaN(value)); // Remove any NaN values
    
    console.log('🔵 modifiedValues after processing:', modifiedValues);
    
    // If values is empty AND we had selections that are no longer visible,
    // this is likely a data change (filter/page change), not a user deselection
    if (modifiedValues.length === 0 && allSelectedIdsRef.current.length > 0) {
      const hasVisibleSelected = allSelectedIdsRef.current.some(id => currentVisibleIds.includes(id));
      if (!hasVisibleSelected) {
        console.log('🔵 Data changed - preserving persistent selection, not clearing');
        // Keep the persistent selection, just show empty for current page
        setSelected([]);
        selectedRef.current = [];
        return; // Don't clear the persistent selection
      }
    }
    
    // This is a real user selection/deselection - update persistent selection
    // Update persistent selection: remove deselected, add newly selected
    // Keep selections from other pages, update current page selections
    allSelectedIdsRef.current = [
      // Keep selections from other pages (not in current visible rows)
      ...allSelectedIdsRef.current.filter(id => !currentVisibleIds.includes(id)),
      // Add all current page selections
      ...modifiedValues
    ];
    
    console.log('🔵 Updated allSelectedIdsRef.current to:', allSelectedIdsRef.current);
    
    // Update ref immediately (synchronously) so it's available right away
    selectedRef.current = modifiedValues;
    console.log('🔵 selectedRef.current updated immediately to:', selectedRef.current);
    
    // Use functional update to ensure we get the latest state
    setSelected((prevSelected) => {
      console.log('🔵 setSelected callback - prevSelected:', prevSelected);
      console.log('🔵 setSelected callback - new modifiedValues:', modifiedValues);
      return modifiedValues;
    });
  };

  // Handle Upload Submission
  const onUploadSubmitting = () => {
    // Use the persistent selection ref which contains ALL selected IDs across all pages/filters
    const currentSelected = allSelectedIdsRef.current.length > 0 
      ? allSelectedIdsRef.current 
      : selectedRef.current.length > 0 
        ? selectedRef.current 
        : selected.length > 0 
          ? selected 
          : [];
    
    console.log('📤 Sending data with selected IDs:', currentSelected);
    console.log('📤 selected state:', selected);
    console.log('📤 selectedRef.current:', selectedRef.current);
    console.log('📤 allSelectedIdsRef.current (persistent):', allSelectedIdsRef.current);
    console.log('📤 Final currentSelected to send:', currentSelected);

    // Validate that we have selected data
    if (!currentSelected || currentSelected.length === 0) {
      dispatch(fetchError('Please select at least one row before sending data.'));
      return;
    }

    // Validate customer ID
    if (!customerId) {
      dispatch(fetchError('Please select a customer before sending data.'));
      return;
    }

    setUpLoadingData(true);

    // Use the selected domain's backend URL
    const baseUrl = domainBackendUrl || jwtAxios.defaults.baseURL;

    sseRequest(`data/send-data/${customerId}`, {
      contentType: 'application/json',
      body: JSON.stringify({dataIds: currentSelected}),
      baseUrl: baseUrl, // Pass the backend URL here
      onmessage: (message) => {
        if (message.event.includes('doneUploadAPI')) {
          setAllStatusCount((prev) => prev + 1);
          setDoneStatusCount((prev) => prev + 1);
        } else if (message.event.includes('errorUploadAPI')) {
          setErrorStatusCount((prev) => prev + 1);
          setAllStatusCount((prev) => prev + 1);
          setErrorData((prev) => {
            prev.push(remanageDataError(message.data));
            return [...prev];
          });
        }
        // Update archiveData by accumulating all id-status pairs
        setArchiveData((prev) => {
          const {id, status} = remanageDataArchive(message.data);
          return {...prev, [id]: {id, status}};
        });
      },
      onclose: () => {
        setUpLoadingData(false);
        setUploadFinished(true); // Set uploadFinished to true
      },
      onerror: (error) => {
        dispatch(fetchError('Failed: ' + error?.message));
        setUpLoadingData(false);
      },
    });
  };

  // Fetch Domains
  const getDomains = (pageSize, pageNumber, search) => {
    const url = `domain/search?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${search}`;
    jwtAxios
      .get(url)
      .then((e) => {
        setDominId(
          e.data.data.map((item) => {
            console.log(item);
            return {
              label: `${item.domain_name}`,
              key: item.id,
              url: item.domain_backend_url, // Store the domain's backend URL
            };
          }),
        );
      })
      .catch((e) => {
        dispatch(fetchError(e.message));
      });
  };

  // Handle Data Deletion
  const deleteData = () => {
    setUpLoadingData(true);
    // Use the persistent selection ref which contains ALL selected IDs
    const currentSelected = allSelectedIdsRef.current.length > 0 
      ? allSelectedIdsRef.current 
      : selectedRef.current.length > 0 
        ? selectedRef.current 
        : selected;
    console.log('🗑️ Deleting data with selected IDs:', currentSelected);
    jwtAxios
      .post(`data/delete-all`, {ids: currentSelected})
      .then(() => getData(pageNumberRef.current, pageSize, appliedFiltersRef.current))
      .catch((e) => dispatch(fetchError(e.message)));
  };

  // Remanage Error Data
  const remanageDataError = (string) => {
    const jsonParsed = JSON.parse(string); // parses entire string to object

    let data;

    if (typeof jsonParsed.data === 'string') {
      try {
        data = JSON.parse(jsonParsed.data); // parses string if needed
      } catch {
        data = jsonParsed.data;
      }
    } else {
      data = jsonParsed.data; // already an object
    }

    // attach error info safely
    data.error = jsonParsed.error || jsonParsed.originalErrorData || null;

    return data;
  };

  // Fetch Customers
  // Inside getCustomers function
  const getCustomers = async (pageSize, pageNumber, search, dominId = null) => {
    try {
      const url = `customer/search-with-ingration-only?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${search}${
        dominId ? `&domainId=${dominId}` : ''
      }`;
      const response = await jwtAxios.get(url);
      const mapping = {};
      const customers = response.data.data.data.map((item) => {
        const customerName = `${item.user_name}${
          item.integration_type === 'trackbox'
            ? ' - ' + JSON.parse(item.additional_data_integration)?.ai
            : ''
        }`;
        mapping[item.id] = customerName;
        return {
          label: customerName,
          key: item.id,
        };
      });
      setAffiliateId(customers);
      setCustomerMapping(mapping); // Store the mapping of customer_id to customer_name
    } catch (e) {
      dispatch(fetchError(e.message));
    }
  };

  // Fetch Customers
  const getCustomersForSend = (
    pageSize,
    pageNumber,
    search,
    dominId = null,
  ) => {
    const url = `customer/search?pageSize=${pageSize}&pageNumber=${pageNumber}&term=${encodeURIComponent(
      search || ''
    )}${dominId ? `&domainId=${encodeURIComponent(dominId)}` : ''}`;

    jwtAxios
      .get(url)
      .then((e) => {
        const mapping = {};
        const customers = e.data.data.data.map((item) => {
          const customerName = `${item.user_name}${
            item.integration_type === 'trackbox'
              ? ' - ' + JSON.parse(item.additional_data_integration)?.ai
              : ''
          }`;
          console.log('customerName');
          console.log(customerName);

          mapping[item.id] = customerName;
          return {
            label: customerName,
            key: item.id,
          };
        });

        setAffiliateIdWithoutNoApi(customers);
        setCustomerMapping(mapping); // Store the mapping of customer_id to customer_name
      })
      .catch((e) => {
        dispatch(fetchError(e.message));
      });
  };
  // Handler for search input changes
  const handleSearchChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      search: event.target.value.trim(), // Trim whitespace
    }));
  };

  // Handler for Date Range change
  const handleDateRangeChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: newValue,
    }));
  };

  // Handler for Sent Date Range change
  const handleSentDateRangeChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      sentDateRange: newValue,
    }));
  };



  // Handle Customer Dropdown Change
  const onDropDownChange = (_, valueSelected) => {
    if (valueSelected && valueSelected.key) {
      setCustomerId(valueSelected.key);
      setSelecteCustomer(true);
    } else {
      // Handle the case when the value is cleared (valueSelected is null or undefined)
      setCustomerId(null);
      setSelecteCustomer(false); // or any other default or fallback behavior you want when the value is cleared
    }
  };

  // Handle Dropdown Search
  const onDropDownSearch = (searchField) => {
    setSearchTerm(searchField.target.value);
  };

  // Handle Clearing Upload Modal Status
  const handleClear = () => {
    setErrorStatusCount(0);
    setAllStatusCount(0);
    setErrorData([]);
    setDoneStatusCount(0);
  };

  const handleGenerateReport = async (reportId) => {
    try {
      const response = await exportDataToExcle(reportId); // This should return the response object

      // Since the response is already parsed, directly destructure the needed values
      const {data, fileName} = response; // Get JSON data directly

      // Create a Blob from the Base64 string
      const byteCharacters = atob(data); // Decode the Base64 string
      const byteNumbers = new Uint8Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([byteNumbers], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Use FileSaver to save the blob as a file
      saveAs(blob, fileName);
      console.log('Report downloaded successfully:', fileName);
    } catch (error) {
      console.error('Error generating report:', error);
      dispatch(fetchError(error.message)); // Display error message in the UI
    }
  };

  const exportDataToExcle = async () => {
    const filterParams = filters;
    setExportingData(true);

    // Construct base query - export data sent to single customer only
    let query = `data/export-single-customer-sent-data-to-excel`;
    const params = [];

    if (filterParams.customer)
      params.push(`customer=${encodeURIComponent(filterParams.customer)}`);
    if (filterParams.country)
      params.push(`country=${encodeURIComponent(filterParams.country)}`);
    if (filterParams.domain)
      params.push(`domainId=${encodeURIComponent(filterParams.domain)}`);
    if (filterParams.dataSource)
      params.push(`dataSource=${encodeURIComponent(filterParams.dataSource)}`);
    if (filterParams.search)
      params.push(`searchParamsData=${encodeURIComponent(filterParams.search)}`);
    if (filterParams.integrationSource)
      params.push(`integrationSource=${encodeURIComponent(filterParams.integrationSource)}`);
    if (filterParams.dateRange) {
      const [startDate, endDate] = filterParams.dateRange;
      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          const dd = d instanceof Date ? new Date(d) : new Date(d);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };
      const from = normalizeToDayIso(startDate, false);
      const to = normalizeToDayIso(endDate, true);
      if (from && to) {
        params.push(`fromDate=${encodeURIComponent(from)}`);
        params.push(`toDate=${encodeURIComponent(to)}`);
      }
    }

    // Add parameters to the query string
    if (params.length) {
      query += `?${params.join('&')}`;
    }

    console.log('Final Query:', query);

    try {
      const response = await jwtAxios.get(query);
      return response.data; // The response should contain { data, fileName }
    } catch (error) {
      console.error('Error exporting data:', error);
      throw new Error(error.response?.data?.message || 'Failed to export data');
    } finally {
      setExportingData(false);
    }
  };

  // Handle Open Lead History
  const handleOpenLeadHistory = (row) => {
    setHistoryLeadId(row?.id);
    setHistoryIsDuplicate(false);
    setHistoryModalOpen(true);
  };

  // Handle Close Lead History
  const handleCloseLeadHistory = () => {
    setHistoryModalOpen(false);
    setHistoryLeadId(null);
    setHistoryIsDuplicate(false);
  };

  return (
    <>
      <AppInfoView />
      {/* Data UI */}
      <DataUI
        // New Props for Lead History
        historyModalOpen={historyModalOpen}
        historyLeadId={historyLeadId}
        historyIsDuplicate={historyIsDuplicate}
        handleOpenLeadHistory={handleOpenLeadHistory}
        handleCloseLeadHistory={handleCloseLeadHistory}

        dataToShow={dataToShow}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        rowCount={rowCount}
        dominId={dominId}
        onFilterChange={handleApplyFilters} // Changed to handleApplyFilters
        onEditCommit={onEditCommit}
        setPatchEdited={setPatchEdited}
        gettingData={gettingData}
        handleOnSelect={handleOnSelect}
        handleChange={handleChange}
        handleDateRangeChange={handleDateRangeChange}
        handleSentDateRangeChange={handleSentDateRangeChange}
        isFilterCardVisible={isFilterCardVisible}
        formatDate={formatDate} // Add formatDate prop
        selected={selected}
        domainSelected={domainSelected}
        handleDomainChange={handleDomainChange}
        handleCustomerChange={handleCustomerChange} // Pass customer change handler (for filters)
        handleSendModalDomainChange={handleSendModalDomainChange} // Separate handler for send modal
        allStatusCount={allStatusCount}
        doneStatusCount={doneStatusCount}
        errorStatusCount={errorStatusCount}
        setShowUploadModal={setShowUploadModal}
        handleCloseModal={handleCloseModal}
        showUploadModal={showUploadModal}
        onUploadSubmitting={onUploadSubmitting}
        onDropDownChange={onDropDownChange}
        onDropDownSearch={onDropDownSearch}
        selecteCustomer={selecteCustomer}
        affiliateId={affiliateIdWithoutNoApi}
        affiliateIdWithoutNoApi={affiliateId}
        errorData={errorData}
        userRole={user.user.role} // Ensure this is an array
        upLoadingData={upLoadingData}
        handleClear={handleClear}
        customerMapping={customerMapping} // Pass customer mapping here
        deleteData={deleteData}
        toggleFilterCardVisibility={toggleFilterCardVisibility}
        disableDataSource={disableDataSource}
        // Pass filter-related props
        filters={filters}
        exportingModalOpen={exportingModalOpen}
        exportingModal={exportingModal}
        exportingModalClose={exportingModalClose}
        setFilters={setFilters}
        scorcessOptions={scorcessOptions}
        integrationSourceOptions={integrationSourceOptions}

        handleSearchChange={handleSearchChange}
        includeHighlighted={includeHighlighted}
        handleIncludeHighlightedChange={handleIncludeHighlightedChange}
        handleClearFilters={handleClearFilters}
        exportDataToExcle={handleGenerateReport}
        archiveData={archiveData} // Pass archiveData as a prop to eliminate linter warning
        exportingData={exportingData}
        // Preserve and control sorting
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        // Note: If DataUI needs more filter options, pass them accordingly
      />
    </>
  );
};

export default DataLG;
